'use client';

/**
 * ToolApprovalCard Component
 *
 * Inline HITL (Human In The Loop) approval card rendered within Astor chat messages.
 * Shown when Astor wants to perform a write operation and needs user permission.
 *
 * Features:
 * - Action title and description with field preview
 * - Warnings section (amber background)
 * - Approve / Edit / Cancel buttons (GlassButton)
 * - Expiration countdown timer with animated progress bar
 * - Risk level indicator (color-coded: green/amber/red)
 * - Reduced motion support
 * - Full i18n support
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, Box, LinearProgress, Typography, useTheme } from '@mui/material';
import type { MatchLevel } from '@ops-matrix/shared-types';
import { AnimatePresence, type Easing, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '../../hooks/useI18n';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { glassEffects } from '../../theme/shadows';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton/GlassButton';

// ============================================================================
// TYPES
// ============================================================================

export interface ToolApprovalCardProps {
  /** Unique identifier for this action */
  actionId: string;
  /** Display title for the action */
  title: string;
  /** Structured preview of what will happen */
  preview: {
    title: string;
    description: string;
    fields: Array<{ label: string; value: string }>;
    warnings: string[];
  };
  /** Risk level of the action */
  riskLevel: `${MatchLevel}`;
  /** Seconds until this approval request expires */
  expiresInSeconds: number;
  /** Called when user approves the action */
  onApprove: (actionId: string) => void;
  /** Called when user wants to edit the action parameters */
  onEdit: (actionId: string) => void;
  /** Called when user cancels/rejects the action */
  onCancel: (actionId: string) => void;
  /** Whether the card buttons should be disabled */
  disabled?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const RISK_COLORS = {
  low: 'success.main',
  medium: 'warning.main',
  high: 'error.main',
} as const;

const RISK_BG_ALPHA = {
  low: 0.08,
  medium: 0.1,
  high: 0.12,
} as const;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const cardVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: [0.215, 0.61, 0.355, 1] as Easing },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2, ease: 'easeIn' as Easing },
  },
};

const reducedVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

// ============================================================================
// HELPER: FORMAT REMAINING TIME
// ============================================================================

function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ToolApprovalCard({
  actionId,
  title,
  preview,
  riskLevel,
  expiresInSeconds,
  onApprove,
  onEdit,
  onCancel,
  disabled = false,
}: ToolApprovalCardProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const prefersReducedMotion = useReducedMotion();
  const isDark = theme.palette.mode === 'dark';
  const glass = isDark ? glassEffects.dark : glassEffects.light;

  // Countdown timer state
  const [remaining, setRemaining] = useState(expiresInSeconds);
  const [expired, setExpired] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start countdown on mount
  useEffect(() => {
    setRemaining(expiresInSeconds);
    setExpired(false);

    timerRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          setExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [expiresInSeconds]);

  // Auto-cancel when expired (design doc: auto-rejection on timeout)
  useEffect(() => {
    if (expired && !disabled) {
      onCancel(actionId);
    }
  }, [expired, disabled, onCancel, actionId]);

  // Progress (1.0 = full, 0.0 = expired)
  const progress = useMemo(
    () => (expiresInSeconds > 0 ? (remaining / expiresInSeconds) * 100 : 0),
    [remaining, expiresInSeconds],
  );

  const handleApprove = useCallback(() => onApprove(actionId), [onApprove, actionId]);
  const handleEdit = useCallback(() => onEdit(actionId), [onEdit, actionId]);
  const handleCancel = useCallback(() => onCancel(actionId), [onCancel, actionId]);

  const variants = prefersReducedMotion ? reducedVariants : cardVariants;
  const isDisabled = disabled || expired;

  const riskColor = RISK_COLORS[riskLevel];
  const riskBgAlpha = RISK_BG_ALPHA[riskLevel];

  return (
    <AnimatePresence mode="wait">
      <Box
        component={prefersReducedMotion ? 'div' : motion.div}
        {...(!prefersReducedMotion && {
          variants,
          initial: 'hidden',
          animate: 'visible',
          exit: 'exit',
        })}
        data-testid="tool-approval-card"
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          WebkitBackdropFilter: glass.backdropBlur,
          border: `1px solid ${glass.border}`,
          boxShadow: glass.shadow,
          maxWidth: 360,
          width: '100%',
        }}
      >
        {/* Risk indicator bar */}
        <Box
          sx={{
            height: 3,
            bgcolor: riskColor,
            width: '100%',
          }}
        />

        {/* Header with title and risk badge */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
              {t('astor.approval.title')}
            </Typography>
            <Box
              data-testid="risk-badge"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: (thm) =>
                  alpha(
                    thm.palette[riskLevel === 'low' ? 'success' : riskLevel === 'medium' ? 'warning' : 'error'].main,
                    riskBgAlpha,
                  ),
                color: riskColor,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.6875rem', textTransform: 'uppercase' }}>
                {riskLevel}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', fontSize: '0.8125rem' }}>
            {title}
          </Typography>
        </Box>

        {/* Field preview */}
        {preview.fields.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              mx: 1.5,
              mb: 1,
              borderRadius: 1.5,
              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            }}
          >
            {preview.fields.map((field, idx) => (
              <Box
                key={`field-${idx}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  py: 0.5,
                  ...(idx < preview.fields.length - 1 && {
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  }),
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {field.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textAlign: 'right', ml: 2 }}>
                  {field.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Warnings */}
        {preview.warnings.length > 0 && (
          <Box
            data-testid="warnings-section"
            sx={{
              mx: 1.5,
              mb: 1,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: (thm) => alpha(thm.palette.warning.main, isDark ? 0.12 : 0.08),
              border: (thm) => `1px solid ${alpha(thm.palette.warning.main, 0.2)}`,
            }}
          >
            {preview.warnings.map((warning, idx) => (
              <Box key={`warning-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.25 }}>
                <WarningAmberIcon
                  sx={{
                    fontSize: iconSizes.xs,
                    color: 'warning.main',
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'warning.main',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                  }}
                >
                  {warning}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Countdown timer */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: expired ? 'error.main' : 'text.secondary',
              }}
            >
              {t('astor.approval.expiresIn', { time: formatTimeRemaining(remaining) })}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            data-testid="countdown-bar"
            sx={{
              height: 3,
              borderRadius: 1.5,
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 1.5,
                bgcolor: expired ? 'error.main' : remaining < 30 ? 'warning.main' : 'success.main',
                transition: 'transform 1s linear',
              },
            }}
          />
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 2,
            pb: 2,
            pt: 0.5,
          }}
        >
          <GlassButton
            variant="gradient"
            color="success"
            size="small"
            disabled={isDisabled}
            onClick={handleApprove}
            data-testid="approve-button"
            sx={{ flex: 1 }}
          >
            {t('astor.approval.approve')}
          </GlassButton>
          <GlassButton
            variant="outlined"
            color="info"
            size="small"
            disabled={isDisabled}
            onClick={handleEdit}
            data-testid="edit-button"
          >
            {t('astor.approval.edit')}
          </GlassButton>
          <GlassButton
            variant="ghost"
            color="error"
            size="small"
            disabled={isDisabled}
            onClick={handleCancel}
            data-testid="cancel-button"
          >
            {t('astor.approval.cancel')}
          </GlassButton>
        </Box>
      </Box>
    </AnimatePresence>
  );
}

ToolApprovalCard.displayName = 'ToolApprovalCard';

export default ToolApprovalCard;
